import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { Chat } from "./chat";
import NoDialog from "./chat/no-dialog";
import { Sidebar } from "./sidebar";
import { iDialogMessagesFile } from "./use-omnichannel";
import { Button } from "primereact/button";
import OmnichannelService from "../services/omnichannelService";
import "./omnichannel.css";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { setLoadOmnichannel } from "../redux/slices/workspaces";
import {
  setWhatsappQrCode,
  setRefreshAllDialogs
} from "../redux/slices/omnichannel";
import { setNewValueDialogId } from "../redux/slices/flowInteractions";
import { ProgressSpinner } from "primereact/progressspinner";
import useDialog from "./chat/use-dialog";
import {
  setNameContact,
  setContactId,
  setSelectedContactAvatarUrl
} from "../redux/slices/omnichannel";
import { AuthContextType, useAuth } from "../auth/auth-provider";
import { useToast } from "../components";
import {
  clipboardHasFiles,
  dataTransferHasFiles,
  emitOmnichannelComposerFiles,
  extractFilesFromClipboard,
  extractFilesFromDataTransfer
} from "./chat/composer-file-events";
import {
  OmnichannelDropOverlay,
  OmnichannelDropOverlayCard
} from "./chat/styles";
import { confirmDialog } from "primereact/confirmdialog";

const Omnichannel = () => {
  const { findDialogHistoryData } = OmnichannelService();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [dialog, setDialog] = useState<iDialogMessagesFile | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [shouldUpdateSidebar, setShouldUpdateSidebar] = useState(false);
  const [dialogId, setDialogId] = useState<number>(0);
  const [chatsSideBarState, setExpandedChat] = useState(true);
  const [screenMobile, setScreenMobile] = useState(false);
  const sidebarChats = useAppSelector((state) => state.sidebar.sidebarChats);
  const allDialogs = useAppSelector((state) => state.sidebar.allDialogs);
  const [botSelected, setBotSelected] = useState<number | null>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragDepthRef = useRef(0);
  const { StartDialogFromContact } = useDialog();
  const { user } = useAuth() as AuthContextType;
  const toast = useToast();
  const updateShowInput = useAppSelector(
    (state) => state.sidebar.updateShowInput
  );
  const refreshAllDialogs = useAppSelector(
    (state) => state.omnichannel.refreshAllDialogs
  );

  const contactId = useAppSelector((state) => state.omnichannel.contactId);
  const isUploadingFile = useAppSelector(
    (state) => state.omnichannel.isUploadingFile
  );

  const confirmLeaveWhileUploading = (onConfirm: () => void) => {
    if (!isUploadingFile) {
      onConfirm();
      return;
    }

    confirmDialog({
      message:
        "Há um arquivo sendo enviado. Se você sair agora, o envio será perdido. Deseja continuar?",
      header: "Enviando arquivo",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sair mesmo assim",
      rejectLabel: "Cancelar",
      accept: onConfirm
    });
  };

  const run = async () => {
    setIsLoading(true);
    try {
      const dialogIdLocal = location?.state?.dialogId || dialogId;
      if (dialogIdLocal) {
        const valueDialog = await findDialogHistoryData(dialogIdLocal);
        setDialog(valueDialog);
      } else {
        setDialog(undefined);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    run();
  }, [updateShowInput]);

  useEffect(() => {
    const expanded = location?.state?.showSidebar !== false;

    setExpandedChat(expanded);
  }, [location.state?.showSidebar]);

  useEffect(() => {
    if (window.innerWidth < 1000) {
      setExpandedChat(false);
    }
  }, []);

  useEffect(() => {
    if (location?.state?.dialogId !== undefined) {
      setDialogId(location.state.dialogId);
    }
  }, [location.state?.dialogId]);

  useEffect(() => {}, [location.state?.showSidebar]);

  useEffect(() => {
    setShouldScroll(false);
  }, [shouldScroll]);

  useEffect(() => {
    if (dialog?.whatsappQrCode) {
      dispatch(setWhatsappQrCode(true));
    } else {
      dispatch(setWhatsappQrCode(false));
    }
  }, [dialog]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 500) {
        setScreenMobile(true);
      } else {
        setScreenMobile(false);
      }
    }
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (sidebarChats) {
      setExpandedChat(true);
    }
  }, [sidebarChats]);
  useEffect(() => {
    if (!refreshAllDialogs) return;

    const currentId = dialog?.id || dialogId || location?.state?.dialogId || 0;

    (async () => {
      if (allDialogs.length === 0) {
        setDialog(undefined);
        setDialogId(0);
        return;
      }

      if (currentId && !allDialogs.includes(currentId)) {
        await openDialogById(allDialogs[0]);
        return;
      }
    })().finally(() => {
      dispatch(setRefreshAllDialogs(false));
    });
  }, [allDialogs, refreshAllDialogs]);

  const openDialogById = async (id: number) => {
    const dialog = await findDialogHistoryData(id);
    setDialogId(id);
    setDialog(dialog);
    setShouldScroll(true);
    dispatch(setNewValueDialogId(0));
    setShouldUpdateSidebar(true);
  };

  const refreshCurrentDialogAndSidebar = async () => {
    const currentId = dialog?.id || dialogId || location?.state?.dialogId || 0;

    if (!currentId) {
      return;
    }

    const updatedDialog = await findDialogHistoryData(currentId);
    setDialog(updatedDialog);
    setShouldUpdateSidebar(true);
  };

  const startDialogFromContact = async (payload: any) => {
    const result = await StartDialogFromContact(payload);
    const status = result?.status;

    if (status === 400) {
      toast.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro.",
        detail:
          result?.response.message || "Não foi possível iniciar o diálogo."
      });
      return result;
    }

    const newDialogId = result?.response?.dialogId;

    const currentOpenId =
      dialog?.id || dialogId || location?.state?.dialogId || 0;

    if ((status === 200 || status === 201) && newDialogId) {
      dispatch(setContactId(0));
      dispatch(setNameContact(""));
      dispatch(setSelectedContactAvatarUrl(""));
      setBotSelected(null);

      if (newDialogId !== currentOpenId) {
        await openDialogById(newDialogId);
      }
    } else if (status === 200) {
      dispatch(setContactId(0));
    }

    return result;
  };

  useEffect(() => {
    setBotSelected(null);
  }, [contactId]);

  useEffect(() => {
    if (!botSelected) return;
    if (contactId === 0) return;

    startDialogFromContact({
      userId: user.id,
      botId: botSelected,
      contactId
    });
  }, [botSelected, contactId]);

  useEffect(() => {
    setContactId(0);
    setNameContact("");
  }, []);

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (!clipboardHasFiles(event.clipboardData)) {
        return;
      }

      const files = extractFilesFromClipboard(event.clipboardData);
      if (!files.length) {
        return;
      }

      event.preventDefault();
      emitOmnichannelComposerFiles(files);
    };

    const handleDragEnter = (event: DragEvent) => {
      if (!dataTransferHasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current += 1;
      setIsDraggingFiles(true);
    };

    const handleDragOver = (event: DragEvent) => {
      if (!dataTransferHasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
      setIsDraggingFiles(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      if (!dataTransferHasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);

      if (dragDepthRef.current === 0) {
        setIsDraggingFiles(false);
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!dataTransferHasFiles(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingFiles(false);

      const files = extractFilesFromDataTransfer(event.dataTransfer);
      if (files.length) {
        emitOmnichannelComposerFiles(files);
      }
    };

    window.addEventListener("paste", handlePaste);
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("paste", handlePaste);
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  return (
    <div
      css={css`
        display: flex;
        height: 100%;
        background: #ffffff;
        overflow: none;
      `}
    >
      {isDraggingFiles ? (
        <OmnichannelDropOverlay>
          <OmnichannelDropOverlayCard
            $mainColor={user.plataform.mainColor || "#2624CD"}
          >
            Solte aqui para anexar
          </OmnichannelDropOverlayCard>
        </OmnichannelDropOverlay>
      ) : null}
      <div className="chatBarHolder">
        {chatsSideBarState ? (
          <div
            style={{ height: "100%" }}
            onClick={async () => {
              if (contactId !== 0) {
                dispatch(setContactId(0));
              }
            }}
          >
            <Sidebar
              shouldUpdate={shouldUpdateSidebar}
              onShouldUpdateChange={() => setShouldUpdateSidebar(false)}
              dialog={dialog}
              onDialogClick={(id) => {
                confirmLeaveWhileUploading(async () => {
                  if (id !== dialog?.id) {
                    await openDialogById(id);
                  }
                });
              }}
            />
          </div>
        ) : null}

        {!screenMobile && (
          <Button
            className="p-button-secondary openCloseButton"
            onClick={() => {
              setExpandedChat((previous) => !previous);
            }}
            label={chatsSideBarState ? "<" : ">"}
            css={
              chatsSideBarState
                ? css`
                    right: -19px;
                  `
                : css`
                    left: -2px;
                  `
            }
          />
        )}
      </div>
      {isLoading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
          `}
        >
          <ProgressSpinner />
        </div>
      ) : dialog ? (
        <Chat
          key={dialog.id}
          dialog={dialog}
          onDialogFinalize={() => setShouldUpdateSidebar(true)}
          shouldScroll={shouldScroll}
          onShouldScroll={setShouldScroll}
          onDialogChange={openDialogById}
          onDialogRefresh={refreshCurrentDialogAndSidebar}
          botSelected={botSelected}
          onBotSelectedChange={setBotSelected}
          startDialogFromContact={startDialogFromContact}
        />
      ) : (
        <NoDialog
          botSelected={botSelected}
          onBotSelectedChange={setBotSelected}
          startDialogFromContact={startDialogFromContact}
        />
      )}
    </div>
  );
};

export default Omnichannel;
